import type { StoredTokens, components } from "@breenova/api-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { api, describeDevice, tokenStore } from "@/lib/api";

/**
 * The signed-in breeder, exactly as the API describes them.
 *
 * Taken from the generated schema rather than declared here: this used to be a
 * hand-written interface the response was cast to, which meant a field added on the
 * server arrived silently instead of as a type error.
 */
export type CurrentUser = components["schemas"]["CurrentUserResponse"];

export interface RegisterInput {
  email: string;
  password: string;
  displayName: string;
  countryCode: string;
  federationId?: string | null;
  ringCode?: string | null;
}

export type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  status: AuthStatus;
  user: CurrentUser | null;

  /**
   * Whether an administrator has let this account in.
   *
   * Not a fourth `AuthStatus`. Waiting is a property of the breeder, not of the session:
   * they are signed in, their token works, and there is exactly one screen they may see.
   * Folding it into the status would make every `status === "authenticated"` check in the
   * app quietly mean something narrower than it says.
   */
  isApproved: boolean;
  isAdmin: boolean;

  signIn: (email: string, password: string) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  signOut: () => Promise<void>;

  /**
   * Re-reads the signed-in breeder. Called after the profile screen saves, so the name
   * in the shell is the stored one rather than the one that was typed.
   */
  refreshUser: () => Promise<void>;

  /**
   * Exchanges the refresh token for a fresh pair, and re-reads the breeder.
   *
   * The waiting screen needs this. Approval is stored on the account but *carried* on the
   * access token, so learning of the decision is not the same as being able to act on it
   * — the token in hand still says the breeder is waiting until it is exchanged for one
   * that does not.
   */
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Holds the session for the whole app.
 *
 * Status starts as `loading` rather than `anonymous`, and that distinction is the whole
 * point: a stored token has to be exchanged for a profile before we know whether it is
 * still good. Treating "not yet checked" as "signed out" would bounce every returning
 * breeder to the login screen for a frame before snapping back.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>(() =>
    tokenStore.read() ? "loading" : "anonymous",
  );
  const [user, setUser] = useState<CurrentUser | null>(null);

  const loadUser = useCallback(async (): Promise<void> => {
    // The fetch layer attaches the token and will silently refresh once if it has
    // expired, so a 401 here means the session is genuinely over.
    const { data, error } = await api.GET("/api/v1/auth/me");

    if (error || !data) {
      tokenStore.clear();
      setUser(null);
      setStatus("anonymous");
      return;
    }

    setUser(data);
    setStatus("authenticated");
  }, []);

  const refreshSession = useCallback(async (): Promise<void> => {
    const refreshToken = tokenStore.read()?.refreshToken;

    if (!refreshToken) {
      return;
    }

    const { data } = await api.POST("/api/v1/auth/refresh", {
      body: { refreshToken, device: describeDevice() ?? null },
    });

    if (data?.tokens) {
      tokenStore.write(data.tokens as StoredTokens);
    }

    // Either way: if the exchange failed the profile read below decides what happens,
    // and a dead session ends up signed out rather than stuck on a screen that retries.
    await loadUser();
  }, [loadUser]);

  // On boot, and whenever another tab signs in or out.
  useEffect(() => {
    if (tokenStore.read()) {
      void loadUser();
    }

    return tokenStore.subscribe((tokens: StoredTokens | null) => {
      if (tokens) {
        return;
      }

      // Cleared elsewhere — another tab signed out, or a refresh failed.
      setUser(null);
      setStatus("anonymous");
      queryClient.clear();
    });
  }, [loadUser, queryClient]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const { data, error } = await api.POST("/api/v1/auth/login", {
        // The generated contract models an absent device as null, not undefined.
        body: { email, password, device: describeDevice() ?? null },
      });

      if (error || !data) {
        throw new Error(messageFrom(error, "Email or password is incorrect."));
      }

      tokenStore.write(data as StoredTokens);
      await loadUser();
    },
    [loadUser],
  );

  const register = useCallback(
    async (input: RegisterInput): Promise<void> => {
      const { data, error } = await api.POST("/api/v1/auth/register", {
        body: {
          ...input,
          federationId: input.federationId || null,
          ringCode: input.ringCode || null,
          // The breeder's own zone, so dates render in their local time without asking.
          timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
          // The generated contract models an absent device as null, not undefined.
          device: describeDevice() ?? null,
        },
      });

      if (error || !data) {
        throw new Error(messageFrom(error, "Could not create the account."));
      }

      tokenStore.write(data as StoredTokens);
      await loadUser();
    },
    [loadUser],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const refreshToken = tokenStore.read()?.refreshToken;

    // Local state is cleared first and unconditionally. If the network call fails the
    // breeder must still end up signed out on this device; the server-side token then
    // expires on its own.
    tokenStore.clear();
    setUser(null);
    setStatus("anonymous");
    queryClient.clear();

    if (refreshToken) {
      try {
        await api.POST("/api/v1/auth/logout", { body: { refreshToken, allDevices: false } });
      } catch {
        // Already signed out locally; nothing useful to show the breeder.
      }
    }
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isApproved: user?.approvalStatus === "Approved",

      // Read off the profile, which the API sends; the API enforces it independently.
      // Hiding the link is a courtesy to the other breeders, not a control.
      isAdmin: user?.roles?.includes("Admin") ?? false,

      signIn,
      register,
      signOut,
      refreshUser: loadUser,
      refreshSession,
    }),
    [status, user, signIn, register, signOut, loadUser, refreshSession],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider.");
  }

  return context;
}

/** Pulls a readable message out of an RFC 9457 problem-details body. */
function messageFrom(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const problem = error as { detail?: string; title?: string; errors?: Record<string, string[]> };

    if (problem.errors) {
      const first = Object.values(problem.errors).flat()[0];
      if (first) {
        return first;
      }
    }

    return problem.detail ?? problem.title ?? fallback;
  }

  return fallback;
}
